// PLOW — the standby rota export.
//
// The "24-7 Rota" spreadsheet listed a row per day with a name and a mobile
// number under each standby level, and was copied round by hand. A board's
// Standby row already holds who is on standby at which level on which day, so
// an editor picks a date range and downloads the rota as one self-contained
// HTML file. Opened on any day, the file works out today in UK time and puts
// today's cover first with tap-to-call numbers; the range follows as week
// strips, and a click on any cell shows that person's contact details.
//
// Two pure functions: buildStandbyRota turns the endpoint's rows into a model
// (days, weeks, levels in use, people, gaps) and renderStandbyRotaHtml turns
// the model into the document. Neither touches the DOM, so both are tested by
// running them under Node. Everything the file needs is inline: no fonts,
// scripts or styles are fetched when it is opened, because it will be opened
// from a phone, an email attachment or a shared drive with no server behind it.
import { standbySlotFor, STANDBY_SLOT_TYPES } from './standby.js';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const isISODate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || '')) && !isNaN(new Date(`${s}T00:00:00Z`));
const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
const toISO = (d) => d.toISOString().slice(0, 10);
export const addDays = (iso, n) => { const d = toDate(iso); d.setUTCDate(d.getUTCDate() + n); return toISO(d); };
/** The Monday on or before a date. */
export const mondayOf = (iso) => { const d = toDate(iso); const dow = d.getUTCDay(); return addDays(iso, dow === 0 ? -6 : 1 - dow); };
/** "7 September 2026", house style, locale-free so Node and the browser agree. */
export const fmtLong = (iso) => { const d = toDate(iso); return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`; };
const fmtDayName = (iso) => DAYS[toDate(iso).getUTCDay()];

/** Two letters for a grid cell: first letters of the first and last words of the name. */
export function initialsOf(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0], last = words[words.length - 1];
  const letter = (w) => (w.match(/[A-Za-z]/) || ['?'])[0].toUpperCase();
  return words.length === 1 ? letter(first) : letter(first) + letter(last);
}

/**
 * The default range the dialog opens with: from this week's Monday for eight
 * weeks, cut short at the last day standby is planned for, but never less
 * than the current week, so there is always something to show.
 */
export function defaultStandbyRotaRange(todayISO, lastPlanned) {
  const from = mondayOf(todayISO);
  const eightWeeks = addDays(from, 55);
  const thisWeek = addDays(from, 6);
  let to = eightWeeks;
  if (lastPlanned && isISODate(lastPlanned) && lastPlanned < to) to = lastPlanned;
  if (to < thisWeek) to = thisWeek;
  return { from, to };
}

/**
 * Lay the endpoint's rows out over the range.
 *
 * @param {{ from: string, to: string, slots: Array, holidays: Array }} input
 *   slots: rows from GET /plows/:id/standby-rota (slot_date, level_id,
 *   level_name, level_sort, person_id, person_name, email, work_phone,
 *   personal_mobile, standby_status); holidays: { holiday_date, name }.
 * @returns the model renderStandbyRotaHtml takes: every day of the range with
 *   its cover per level and its standby window; the days grouped into
 *   Monday-keyed weeks; the levels anyone is on, in their configured order;
 *   the people indexed once with their numbers; and the gaps, one per day and
 *   level with nobody on it.
 */
export function buildStandbyRota({ from, to, slots = [], holidays = [] }) {
  if (!isISODate(from) || !isISODate(to) || to < from) throw new Error('The range needs a from and a to date, in order');
  const holidayByDate = Object.fromEntries((holidays || []).map(h => [h.holiday_date, h.name]));
  const isHoliday = (iso) => !!holidayByDate[iso];

  const levelMap = new Map();
  const people = {};
  const coverByDate = new Map();
  let slot_count = 0;
  for (const s of slots) {
    if (!s || !s.slot_date || !s.level_id || s.slot_date < from || s.slot_date > to) continue;
    slot_count++;
    if (!levelMap.has(s.level_id)) {
      levelMap.set(s.level_id, { id: s.level_id, name: s.level_name || 'Standby', sort: s.level_sort ?? 0 });
    }
    if (!people[s.person_id]) {
      people[s.person_id] = {
        id: s.person_id,
        name: s.person_name || 'Unknown',
        initials: initialsOf(s.person_name),
        email: s.email || null,
        work_phone: s.work_phone || null,
        personal_mobile: s.personal_mobile || null,
        dates: [],
      };
    }
    people[s.person_id].dates.push({ date: s.slot_date, level_id: s.level_id });
    if (!coverByDate.has(s.slot_date)) coverByDate.set(s.slot_date, {});
    const cover = coverByDate.get(s.slot_date);
    (cover[s.level_id] ||= []);
    if (!cover[s.level_id].includes(s.person_id)) cover[s.level_id].push(s.person_id);
  }
  const levels = [...levelMap.values()]
    .sort((a, b) => (a.sort - b.sort) || a.name.localeCompare(b.name))
    .map(({ id, name }) => ({ id, name }));
  for (const p of Object.values(people)) p.dates.sort((a, b) => a.date.localeCompare(b.date));

  const days = [];
  const gaps = [];
  for (let iso = from; iso <= to; iso = addDays(iso, 1)) {
    const slot = standbySlotFor(iso, isHoliday);
    const cover = coverByDate.get(iso) || {};
    days.push({
      date: iso,
      day_name: fmtDayName(iso),
      slot_type: slot.slot_type,
      hours: slot.hours,
      holiday: holidayByDate[iso] || null,
      cover,
    });
    for (const l of levels) {
      if (!cover[l.id] || !cover[l.id].length) gaps.push({ date: iso, level_id: l.id, level_name: l.name });
    }
  }

  const weeks = [];
  for (const d of days) {
    const monday = mondayOf(d.date);
    let w = weeks[weeks.length - 1];
    if (!w || w.week_start !== monday) { w = { week_start: monday, dates: [] }; weeks.push(w); }
    w.dates.push(d.date);
  }

  return { from, to, days, weeks, levels, people, gaps, slot_count };
}

/** `standby-rota-<board>-<from>-to-<to>.html` */
export function standbyRotaFilename(plowName, from, to) {
  const slug = String(plowName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'board';
  return `standby-rota-${slug}-${from}-to-${to}.html`;
}

// ── The on-call upload ──────────────────────────────────────────────────────
//
// The incident management system's Upload on-call rota screen takes a
// spreadsheet of Date, Level 1, Number, Level 2, Number, read by column
// position with the header row ignored. It holds one person per level per
// day, matches them by name, username or email, and a blank cell leaves that
// day's existing assignment alone rather than clearing it. Its two levels are
// not ours, so the editor says which board level goes in which column.

const mobileLike = (n) => /^(?:44|0)7\d{9}$/.test(String(n || '').replace(/\D/g, '').replace(/^440/, '44'));

/** The one number the upload has room for: a work mobile, else the personal mobile, else the work phone. */
export function onCallNumber(person) {
  if (!person) return '';
  if (mobileLike(person.work_phone)) return person.work_phone;
  return person.personal_mobile || person.work_phone || '';
}

/** The two levels the upload columns default to: the first two in use, in their configured order. */
export function defaultOnCallLevels(model) {
  return { level1: model.levels[0]?.id || '', level2: model.levels[1]?.id || '' };
}

/**
 * A row per day of the range in the upload's column order. A person is named
 * by work email where People holds one, since that is what the upload matches
 * most reliably, and by full name otherwise.
 *
 * @param model  from buildStandbyRota
 * @param {{ level1: string, level2: string }} columns  level ids, '' for a blank column
 * @returns {{ headers, rows, doubled, gaps, left_out }} doubled: days where a
 *   level holds more than one person, so only the first goes in; gaps: day
 *   and column with nobody; left_out: levels in use that neither column takes.
 */
export function buildOnCallUpload(model, { level1, level2 } = {}) {
  const headers = ['Date', 'Level 1', 'Number', 'Level 2', 'Number'];
  const levelName = (id) => (model.levels.find(l => l.id === id) || {}).name || '';
  const doubled = [];
  const gaps = [];
  const rows = model.days.map(d => {
    const row = [d.date];
    for (const [column, levelId] of [['Level 1', level1], ['Level 2', level2]]) {
      const ids = levelId ? (d.cover[levelId] || []) : [];
      const p = model.people[ids[0]];
      if (levelId && !p) gaps.push({ date: d.date, column });
      if (ids.length > 1) doubled.push({ date: d.date, column, level_name: levelName(levelId), kept: p.name, dropped: ids.slice(1).map(id => model.people[id]?.name || 'Unknown') });
      row.push(p ? (p.email || p.name) : '', p ? onCallNumber(p) : '');
    }
    return row;
  });
  const left_out = model.levels.filter(l => l.id !== level1 && l.id !== level2);
  return { headers, rows, doubled, gaps, left_out };
}

/** The upload as one sheet for xlsxWriter, every cell text so a leading 0 survives. */
export function onCallUploadSheet(upload) {
  const head = { font: { bold: true } };
  return {
    name: 'On-call',
    rows: [upload.headers.map(v => ({ v, s: head })), ...upload.rows],
    cols: [{ wch: 12 }, { wch: 32 }, { wch: 16 }, { wch: 32 }, { wch: 16 }],
    freeze: { xSplit: 0, ySplit: 1 },
  };
}

/** `on-call-rota-<board>-<from>-to-<to>.xlsx` */
export function onCallUploadFilename(plowName, from, to) {
  return standbyRotaFilename(plowName, from, to).replace(/^standby-rota-/, 'on-call-rota-').replace(/\.html$/, '.xlsx');
}

const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const telHref = (n) => `tel:${String(n).replace(/[^\d+]/g, '')}`;
// JSON that is safe inside a <script>: no "</" sequence can appear, and the
// two line terminators JSON allows but JavaScript did not are escaped too.
const scriptJSON = (obj) => JSON.stringify(obj)
  .replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');

const fmtStamp = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const uk = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d);
  const part = (t) => (uk.find(p => p.type === t) || {}).value || '';
  return `${part('day')} ${part('month')} ${part('year')} at ${part('hour')}:${part('minute')}`;
};

/**
 * The document. Complete, starting <!doctype html>, everything inline.
 *
 * @param model  from buildStandbyRota
 * @param {{ plowName: string, generatedAt?: string, generatedBy?: string }} meta
 */
export function renderStandbyRotaHtml(model, { plowName, generatedAt, generatedBy } = {}) {
  const title = `Standby rota: ${plowName || 'PLOW'}, ${fmtLong(model.from)} to ${fmtLong(model.to)}`;
  const windows = `Weekday standby runs ${STANDBY_SLOT_TYPES.weekday_evening.window} the next morning; weekends and bank holidays run ${STANDBY_SLOT_TYPES.weekend_day.window} the next morning.`;
  const stamp = generatedAt ? fmtStamp(generatedAt) : '';
  const exportedLine = [stamp ? `Exported ${stamp}` : '', generatedBy ? `by ${generatedBy}` : ''].filter(Boolean).join(' ');

  const weekHtml = model.weeks.map(w => {
    const head = w.dates.map(iso => {
      const d = model.days.find(x => x.date === iso);
      return `<th class="day${d.slot_type === 'weekend_day' ? ' we' : ''}" data-date="${iso}" scope="col">`
        + `<span class="dn">${esc(d.day_name.slice(0, 3))}</span><span class="dd">${toDate(iso).getUTCDate()} ${MONTHS[toDate(iso).getUTCMonth()].slice(0, 3)}</span>`
        + (d.holiday ? `<span class="bh" title="${esc(d.holiday)}">Bank hol</span>` : '')
        + '</th>';
    }).join('');
    const rows = model.levels.map(l => {
      const cells = w.dates.map(iso => {
        const d = model.days.find(x => x.date === iso);
        const ids = d.cover[l.id] || [];
        const inner = ids.length
          ? ids.map(pid => {
            const p = model.people[pid];
            return `<button type="button" class="cell" data-person="${esc(pid)}" data-date="${iso}" data-level="${esc(l.id)}" title="${esc(p.name)}">`
              + `<span class="ini">${esc(p.initials)}</span><span class="full">${esc(p.name)}</span></button>`;
          }).join('')
          : '<span class="gap" title="Nobody on standby">–</span>';
        return `<td data-date="${iso}" data-level="${esc(l.id)}"${d.slot_type === 'weekend_day' ? ' class="we"' : ''}>${inner}</td>`;
      }).join('');
      return `<tr><th scope="row" class="lvl">${esc(l.name)}</th>${cells}</tr>`;
    }).join('');
    return `<section class="week" data-week="${w.week_start}">
<h2>Week commencing ${fmtLong(w.week_start)}</h2>
<div class="scroll"><table><thead><tr><th class="corner" scope="col"><span class="sr">Level</span></th>${head}</tr></thead><tbody>${rows}</tbody></table></div>
</section>`;
  }).join('\n');

  // Everyone on the rota with their numbers, as plain links: the printed copy
  // and anyone without JavaScript still get the numbers.
  const contactsHtml = Object.values(model.people)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(p => {
      const nums = [
        p.work_phone ? `<a class="tel" href="${esc(telHref(p.work_phone))}">${esc(p.work_phone)}</a> <small>work</small>` : '',
        p.personal_mobile ? `<a class="tel" href="${esc(telHref(p.personal_mobile))}">${esc(p.personal_mobile)}</a> <small>mobile</small>` : '',
      ].filter(Boolean).join(' · ');
      return `<li><button type="button" class="pname" data-person="${esc(p.id)}">${esc(p.name)}</button> ${nums || '<span class="nonum">no number in the directory</span>'}</li>`;
    }).join('');

  const data = {
    from: model.from, to: model.to,
    levels: model.levels,
    people: Object.fromEntries(Object.entries(model.people).map(([id, p]) => [id, {
      name: p.name, initials: p.initials, work_phone: p.work_phone, personal_mobile: p.personal_mobile, dates: p.dates,
    }])),
    days: Object.fromEntries(model.days.map(d => [d.date, { slot_type: d.slot_type, holiday: d.holiday, cover: d.cover, day_name: d.day_name }])),
  };

  return `<!doctype html>
<html lang="en-GB">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light dark">
<title>${esc(title)}</title>
<style>
:root{--navy:#0D1D32;--gold:#A1723D;--gold-light:#E5B87A;--bg:#F7F7F5;--panel:#FFFFFF;--text:#1F2937;--muted:#6B7280;--line:#E5E7EB;--cell:#F3F4F6;--we:#FAF6EE;--today:#FFF4E0;--today-line:#A1723D;--link:#0D1D32;--shadow:0 12px 32px rgba(13,29,50,.25)}
@media (prefers-color-scheme: dark){:root{--bg:#0F172A;--panel:#16213A;--text:#E5E7EB;--muted:#9CA3AF;--line:#2B3A55;--cell:#1F2B45;--we:#1B2540;--today:#3A2E1A;--today-line:#E5B87A;--link:#E5B87A;--shadow:0 12px 32px rgba(0,0,0,.6)}}
*{box-sizing:border-box}
html,body{margin:0;padding:0}
body{font-family:Montserrat,"Segoe UI",Arial,Helvetica,sans-serif;background:var(--bg);color:var(--text);line-height:1.45;font-size:15px}
a{color:var(--link)}
.sr{position:absolute;left:-9999px}
header.top{background:var(--navy);color:#fff;padding:18px 20px 16px;border-bottom:3px solid var(--gold)}
header .brand{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--gold-light);font-weight:600}
header h1{margin:4px 0 2px;font-size:22px;font-weight:600}
header h1 span{color:var(--gold-light);font-weight:500}
header .range{margin:0;font-size:13px;color:#CBD5E1}
main{max-width:1100px;margin:0 auto;padding:16px 16px 40px}
.now{background:var(--panel);border:1px solid var(--line);border-radius:12px;padding:14px 16px 16px;margin-bottom:22px}
.now .kicker{display:flex;flex-wrap:wrap;gap:6px 14px;align-items:baseline;margin:0 0 10px}
.now .kicker strong{font-size:16px}
.now .kicker span{color:var(--muted);font-size:13px}
.now .kicker a{font-size:13px;margin-left:auto}
.cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px}
.card{border:1px solid var(--line);border-left:4px solid var(--gold);border-radius:8px;padding:10px 12px;background:var(--bg)}
.card .lv{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);font-weight:600}
.card .who{font-size:17px;font-weight:600;margin:2px 0 4px}
.card .who button{all:unset;cursor:pointer;color:inherit;border-bottom:1px dotted var(--gold)}
.card .tel{display:block;font-size:15px;text-decoration:none;font-weight:500;margin:2px 0}
.card .tel small{color:var(--muted);font-weight:400;font-size:12px;margin-left:6px}
.card .none{color:var(--muted);font-style:italic}
.outside{color:var(--muted);margin:0}
.week{margin-bottom:18px}
.week h2{font-size:14px;font-weight:600;margin:0 0 6px;color:var(--muted)}
.scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
table{border-collapse:separate;border-spacing:3px;width:100%;min-width:640px}
th,td{padding:0;text-align:center;vertical-align:top}
th.corner{width:120px}
th.lvl{text-align:left;font-size:12px;font-weight:600;padding:8px 6px 0 2px;white-space:nowrap}
th.day{font-weight:500;font-size:12px;padding:4px 2px;border-radius:6px;background:var(--panel);border:1px solid var(--line)}
th.day .dn{display:block;color:var(--muted);font-size:11px}
th.day .dd{display:block;font-weight:600}
th.day .bh{display:inline-block;margin-top:2px;font-size:10px;padding:0 5px;border-radius:999px;background:var(--gold);color:#fff}
th.we,td.we{background:var(--we)}
td{background:var(--cell);border-radius:6px;min-height:40px;height:44px;border:1px solid var(--line)}
td .gap{display:block;padding:10px 0;color:var(--muted)}
.cell{all:unset;display:block;width:100%;padding:6px 4px;cursor:pointer;text-align:center}
.cell:hover,.cell:focus-visible{background:var(--gold-light);color:var(--navy);border-radius:5px;outline:none}
.cell .ini{display:block;font-weight:700;font-size:15px;letter-spacing:.04em}
.cell .full{display:none;font-size:11px}
@media (min-width:900px){.cell .ini{display:none}.cell .full{display:block;font-size:12px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}}
.today th.day,th.day.today{background:var(--today);border-color:var(--today-line);box-shadow:inset 0 -3px 0 var(--today-line)}
td.today{background:var(--today);border-color:var(--today-line)}
.note{font-size:12px;color:var(--muted);margin:18px 0 0}
.legend{font-size:12px;color:var(--muted);display:flex;gap:16px;flex-wrap:wrap;margin:4px 0 0}
.legend i{display:inline-block;width:12px;height:12px;border-radius:3px;vertical-align:-2px;margin-right:5px;border:1px solid var(--line)}
#backdrop{position:fixed;inset:0;background:rgba(13,29,50,.45);display:none;z-index:9}
#panel{position:fixed;left:50%;bottom:0;transform:translateX(-50%);width:min(440px,100%);background:var(--panel);color:var(--text);border-radius:14px 14px 0 0;box-shadow:var(--shadow);padding:16px 18px 20px;display:none;z-index:10}
@media (min-width:640px){#panel{bottom:auto;top:50%;transform:translate(-50%,-50%);border-radius:14px}}
#panel.open,#backdrop.open{display:block}
#panel .close{all:unset;position:absolute;top:10px;right:12px;cursor:pointer;font-size:22px;line-height:1;color:var(--muted);padding:4px}
#panel .head{display:flex;align-items:center;gap:12px;margin-bottom:10px}
#panel .avatar{width:46px;height:46px;border-radius:50%;background:var(--navy);color:var(--gold-light);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:16px;flex:none}
#panel .name{font-size:18px;font-weight:600}
#panel .ctx{font-size:13px;color:var(--muted)}
#panel .tel{display:flex;justify-content:space-between;align-items:center;padding:10px 12px;border:1px solid var(--line);border-radius:8px;text-decoration:none;margin:6px 0;font-weight:600;font-size:16px}
#panel .tel small{font-weight:400;color:var(--muted);font-size:12px}
#panel .nonum{color:var(--muted);font-style:italic;font-size:13px}
#panel .more{font-size:12px;color:var(--muted);margin:10px 0 0}
.contacts{margin-top:26px;border-top:1px solid var(--line);padding-top:12px}
.contacts h2{font-size:14px;font-weight:600;margin:0 0 6px;color:var(--muted)}
.contacts ul{list-style:none;margin:0;padding:0;columns:2;column-gap:24px;font-size:13px}
@media (max-width:640px){.contacts ul{columns:1}}
.contacts li{break-inside:avoid;padding:4px 0}
.contacts .pname{all:unset;cursor:pointer;font-weight:600;border-bottom:1px dotted var(--gold);margin-right:8px}
.contacts .tel{text-decoration:none;font-weight:500}
.contacts small,.contacts .nonum{color:var(--muted)}
@media print{#panel,#backdrop,.now .kicker a{display:none!important}body{background:#fff}header.top{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style>
</head>
<body>
<header class="top">
<div class="brand">Alton Valley</div>
<h1>Standby rota <span>${esc(plowName || 'PLOW')}</span></h1>
<p class="range">${esc(fmtLong(model.from))} to ${esc(fmtLong(model.to))}${exportedLine ? ` · ${esc(exportedLine)}` : ''}</p>
</header>
<main>
<section class="now" id="now" aria-live="polite"><p class="outside">Working out who is on standby now…</p></section>
${weekHtml}
<p class="legend"><span><i style="background:var(--today);border-color:var(--today-line)"></i>Today</span><span><i style="background:var(--we)"></i>Weekend or bank holiday (24 hours)</span><span><i style="background:var(--cell)"></i>Weekday evening (16 hours)</span></p>
<p class="note">${esc(windows)} Click or tap a name for contact numbers. This file was exported from the PLOW and does not update itself: for the latest plan, export it again.</p>
<section class="contacts">
<h2>Contacts</h2>
<ul>${contactsHtml || '<li class="nonum">Nobody is on standby in this range.</li>'}</ul>
</section>
</main>
<div id="backdrop"></div>
<div id="panel" role="dialog" aria-modal="true" aria-labelledby="panel-name"></div>
<script>
var DATA = ${scriptJSON(data)};
(function () {
  var MONTHS = ${scriptJSON(MONTHS)};
  var DAYS = ${scriptJSON(DAYS)};
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function tel(n) { return 'tel:' + String(n).replace(/[^\\d+]/g, ''); }
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function iso(d) { return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate()); }
  function addDays(s, n) { var d = new Date(s + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return iso(d); }
  function fmtLong(s) { var d = new Date(s + 'T00:00:00Z'); return DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()] + ' ' + d.getUTCFullYear(); }
  function ukNow() {
    var parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(new Date());
    var get = function (t) { for (var i = 0; i < parts.length; i++) if (parts[i].type === t) return parts[i].value; return ''; };
    return { date: get('year') + '-' + get('month') + '-' + get('day'), hour: parseInt(get('hour'), 10) || 0 };
  }
  function numbersHtml(p, cls) {
    var out = '';
    if (p.work_phone) out += '<a class="' + cls + '" href="' + tel(p.work_phone) + '">' + esc(p.work_phone) + '<small>work</small></a>';
    if (p.personal_mobile) out += '<a class="' + cls + '" href="' + tel(p.personal_mobile) + '">' + esc(p.personal_mobile) + '<small>mobile</small></a>';
    return out;
  }
  var panel = document.getElementById('panel');
  var backdrop = document.getElementById('backdrop');
  function closePanel() { panel.classList.remove('open'); backdrop.classList.remove('open'); panel.innerHTML = ''; }
  function openPanel(pid, date, levelId) {
    var p = DATA.people[pid]; if (!p) return;
    var level = null; for (var i = 0; i < DATA.levels.length; i++) if (DATA.levels[i].id === levelId) level = DATA.levels[i];
    var day = DATA.days[date];
    var ctx = [];
    if (level) ctx.push(level.name);
    if (date) ctx.push(fmtLong(date) + (day && day.slot_type === 'weekend_day' ? ', 8am to 8am' : ', 6pm to 8am'));
    var nums = numbersHtml(p, 'tel');
    var others = p.dates.length;
    var html = '<button type="button" class="close" aria-label="Close">×</button>'
      + '<div class="head"><div class="avatar">' + esc(p.initials) + '</div><div><div class="name" id="panel-name">' + esc(p.name) + '</div><div class="ctx">' + esc(ctx.join(' · ')) + '</div></div></div>'
      + (nums || '<p class="nonum">No contact number is held in the directory for ' + esc(p.name) + '.</p>')
      + '<p class="more">On standby ' + others + ' day' + (others === 1 ? '' : 's') + ' in this rota.</p>';
    panel.innerHTML = html;
    panel.classList.add('open'); backdrop.classList.add('open');
    panel.querySelector('.close').focus();
  }
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('[data-person]') : null;
    if (b) { openPanel(b.getAttribute('data-person'), b.getAttribute('data-date'), b.getAttribute('data-level')); return; }
    if (e.target.closest && e.target.closest('#panel .close')) closePanel();
  });
  backdrop.addEventListener('click', closePanel);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closePanel(); });

  function renderNow() {
    var now = ukNow();
    var today = now.date;
    var active = now.hour < 8 ? addDays(today, -1) : today;
    var activeDay = DATA.days[active];
    var el = document.getElementById('now');
    var when;
    if (now.hour < 8) when = 'until 8am this morning (' + fmtLong(active) + ' cover)';
    else if (activeDay && activeDay.slot_type === 'weekend_day') when = '8am today to 8am tomorrow';
    else when = '6pm tonight to 8am tomorrow';
    var todayCells = document.querySelectorAll('[data-date="' + today + '"]');
    for (var i = 0; i < todayCells.length; i++) todayCells[i].classList.add('today');
    var head = '<p class="kicker"><strong>On standby now</strong><span>' + esc(fmtLong(today)) + ' · ' + esc(when) + '</span>'
      + (todayCells.length ? '<a href="#" id="jump">Jump to today</a>' : '') + '</p>';
    if (!activeDay) {
      el.innerHTML = head + '<p class="outside">Today is outside this rota, which covers ' + esc(fmtLong(DATA.from)) + ' to ' + esc(fmtLong(DATA.to)) + '. Export it again from the PLOW for the current plan.</p>';
      return;
    }
    var cards = '';
    for (var l = 0; l < DATA.levels.length; l++) {
      var level = DATA.levels[l];
      var ids = activeDay.cover[level.id] || [];
      cards += '<div class="card"><div class="lv">' + esc(level.name) + '</div>';
      if (!ids.length) cards += '<div class="none">Nobody on standby</div>';
      for (var k = 0; k < ids.length; k++) {
        var p = DATA.people[ids[k]]; if (!p) continue;
        cards += '<div class="who"><button type="button" data-person="' + esc(ids[k]) + '" data-date="' + esc(active) + '" data-level="' + esc(level.id) + '">' + esc(p.name) + '</button></div>';
        cards += numbersHtml(p, 'tel') || '<div class="none">No number in the directory</div>';
      }
      cards += '</div>';
    }
    el.innerHTML = head + '<div class="cards">' + cards + '</div>';
    var jump = document.getElementById('jump');
    if (jump) jump.addEventListener('click', function (e) {
      e.preventDefault();
      var t = document.querySelector('th.day.today');
      if (t) t.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    });
  }
  renderNow();
  setInterval(renderNow, 5 * 60 * 1000);
})();
</script>
</body>
</html>
`;
}
